"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

// Storyblok asset URLs have the form `…/<width>x<height>/<hash>/…`.
function storyblokDimensions(url?: string): { width: number; height: number } {
  const fallback = { width: 200, height: 200 };
  if (!url) return fallback;
  const match = url.match(/\/(\d+)x(\d+)\//);
  if (!match) return fallback;
  return { width: Number(match[1]), height: Number(match[2]) };
}

type AwardLogo = SbBlokData & {
  name?: string;
  image?: Asset;
};

type AwardsBlok = SbBlokData & {
  title?: string;
  logos?: AwardLogo[];
  link_label?: string;
  link_url?: StoryblokLink;
  bg_color?: string;
};

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Awards({ blok }: { blok: AwardsBlok }) {
  const logos = blok.logos || [];
  const { bg } = resolveBg(blok.bg_color, "dusk-01");
  const region = regionFromPathname(usePathname());
  const link = resolveLink(blok.link_url, region);
  const showLink = Boolean(blok.link_label && link.href !== "#");

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2rem,4vw,3rem)] pb-[clamp(3.75rem,6vw,4.5rem)]">
        {blok.title && (
          <h2 className="text-purple-05 text-center text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">
            {blok.title}
          </h2>
        )}

        {logos.length > 0 && (
          <div
            role="group"
            aria-label={blok.title || "Awards and accreditations"}
            tabIndex={0}
            className="scrollbar-none focus-visible:outline-purple-03 -mx-[clamp(1.25rem,4vw,2.5rem)] mt-[clamp(2rem,5vw,6.25rem)] overflow-x-auto px-[clamp(1.25rem,4vw,2.5rem)] [-ms-overflow-style:none] focus:outline-none focus-visible:rounded-[12px] focus-visible:outline-2 focus-visible:-outline-offset-2 lg:mx-0 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden"
          >
            <ul className="flex w-max items-center gap-10 lg:w-full lg:justify-between lg:gap-8">
              {logos.map((logo) => {
                const { width, height } = storyblokDimensions(logo.image?.filename);
                return (
                  <li key={logo._uid as string} {...storyblokEditable(logo)} className="shrink-0">
                    {logo.image?.filename && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={`${logo.image.filename}/m/${width}x${height}/filters:quality(80)`}
                        alt={logo.image.alt || logo.name || ""}
                        width={width}
                        height={height}
                        className="h-[105px] w-auto object-contain lg:h-[125px]"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {(logos.length > 0 || showLink) && (
          <hr className="mt-8 border-t border-black/15 lg:mt-[40px]" />
        )}

        {showLink && (
          <div className="mt-6 flex justify-center lg:mt-[18px]">
            <a
              href={link.href}
              {...(link.target ? { target: link.target } : {})}
              className="text-body text-purple-04 inline-flex items-center gap-[7px] leading-[1.054] font-extrabold hover:underline"
            >
              {blok.link_label}
              <ArrowIcon />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
